import { ImpulseElement, property, registerElement, target } from '@ambiki/impulse';
import { baseSortableOptions } from 'helpers/sortable';
// @ts-expect-error No types
import Sortable from 'sortablejs';

const PLACEHOLDER_INFO = 'vs-placeholder-info';
const PLACEHOLDER_SUCCESS = 'vs-placeholder-success';
const PLACEHOLDER_SHOW = 'show-placholder';
const ITEM_SHOW = 'show-item';

const sortableOptions = {
  ...baseSortableOptions,
  group: 'shared',
  sort: false,
  onMove: () => false,
};

@registerElement('vs-plan-completed-item')
export default class VsPlanCompletedItemElement extends ImpulseElement {
  /**
   * The `id` of the card record.
   */
  @property() cardId: string;

  @target() planContainer: HTMLElement;
  @target() completedContainer: HTMLElement;

  private planSortable: Sortable;
  private completedSortable: Sortable;

  // Built per connect rather than once per instance. The element survives a disconnect, so a
  // controller created in the field initializer stays aborted after the first teardown and every
  // listener attached on a later connect is dead on arrival.
  private abortController?: AbortController;

  connected() {
    this.abortController = new AbortController();

    this.setupPlanSortable();
    this.setupCompletedSortable();
  }

  disconnected() {
    // Abort the listener.
    this.abortController?.abort();
    this.planSortable?.destroy();
    this.completedSortable?.destroy();
  }

  restart() {
    this.planContainer.classList.remove(PLACEHOLDER_SHOW);
    this.completedContainer.classList.remove(ITEM_SHOW);
  }

  private setupPlanSortable() {
    this.planSortable = new Sortable(this.planContainer, {
      ...sortableOptions,
      onStart: () => {
        this.addEventListener('drag', this.handleDragOnCompleted.bind(this), { signal: this.abortController?.signal });
        this.completedContainer.classList.add(PLACEHOLDER_INFO);
      },
      onEnd: (e: any) => {
        this.completedContainer.classList.remove(...[PLACEHOLDER_INFO, PLACEHOLDER_SUCCESS]);
        if (!this.isCollidingWithCompletedPlaceholder(e.originalEvent.clientX, e.originalEvent.clientY)) return;
        this.planContainer.classList.add(PLACEHOLDER_SHOW);
        this.completedContainer.classList.add(ITEM_SHOW);
        this.emit('dragend');
      },
    });
  }

  private setupCompletedSortable() {
    this.completedSortable = new Sortable(this.completedContainer, {
      ...sortableOptions,
      onStart: () => {
        this.addEventListener('drag', this.handleDragOnPlan.bind(this), { signal: this.abortController?.signal });
        this.planContainer.classList.add(PLACEHOLDER_INFO);
      },
      onEnd: (e: any) => {
        this.planContainer.classList.remove(...[PLACEHOLDER_INFO, PLACEHOLDER_SUCCESS]);
        if (!this.isCollidingWithPlanPlaceholder(e.originalEvent.clientX, e.originalEvent.clientY)) return;
        this.restart();
        this.emit('dragend');
      },
    });
  }

  private handleDragOnCompleted(e: DragEvent) {
    const isColliding = this.isCollidingWithCompletedPlaceholder(e.clientX, e.clientY);
    this.completedContainer.classList.toggle(PLACEHOLDER_SUCCESS, isColliding);
  }

  private handleDragOnPlan(e: DragEvent) {
    const isColliding = this.isCollidingWithPlanPlaceholder(e.clientX, e.clientY);
    this.planContainer.classList.toggle(PLACEHOLDER_SUCCESS, isColliding);
  }

  private isCollidingWithPlanPlaceholder(x: number, y: number): boolean {
    const elementUnder = document.elementFromPoint(x, y);
    return this.contains(elementUnder) && !!elementUnder?.closest('.vs-plan-container .vs-placeholder');
  }

  private isCollidingWithCompletedPlaceholder(x: number, y: number): boolean {
    const elementUnder = document.elementFromPoint(x, y);
    return this.contains(elementUnder) && !!elementUnder?.closest('.vs-completed-container .vs-placeholder');
  }
}
